import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from '@ebw-monorepo/login';
import { EBWModalType } from '@ebw-monorepo/modal';
import { NavigationService } from '@ebw-monorepo/navigation';
import { Observable } from 'rxjs';
import { I18NService } from './../../../../i18n-translate/src/lib/services/i18n.service';
import { EbwModalService } from './../../../../modal/src/lib/modal/service/ebw-modal.service';
import { LoaderService } from './../services/loader.service';
import { SKIP_LOADING_SPINNER } from './../tokens/skip-loading-spinner.token';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  private requests: HttpRequest<any>[] = [];

  i18nService: I18NService;

  constructor(
    private loaderService: LoaderService,
    private ebwModalService: EbwModalService,
    private loginService: LoginService,
    private navigation: NavigationService,
    private injector: Injector,
    private router: Router
  ) {
    setTimeout(() => {
      this.i18nService = this.injector.get(I18NService);
    });
  }

  // va chiamato solo per richieste effettivamente tracciate (shouldTrack in intercept()): mai per
  // richieste escluse/skippate, altrimenti this.requests conterebbe anche quelle e riaccenderebbe
  // lo spinner (length > 0) senza che nessuna richiesta "reale" lo giustifichi
  removeRequest(req: HttpRequest<any>) {
    const i = this.requests.indexOf(req);
    if (i >= 0) {
      this.requests.splice(i, 1);
    }
    this.loaderService.isLoading.next(this.requests.length > 0);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const { url } = req;
    // Controllo le chiamate per cui non mostrare lo spinner di caricamento
    const excludedLoadingRequest = [
      '/auth/refreshToken',
      '/psa/matriceRischio?id=',
      '/psa/statoAvanzamento?id=',
      '/notification/get', // TODO da rimuovere quando ci sarà la ws funzionante
      '/controlli/antea/elaborazione/' // polling di stato ogni 5s, mostra già un indicatore locale nella tab
    ];

    const isUrlExcludedFromLoadingSpinner = excludedLoadingRequest.some((path: string) => url.includes(path));
    // tile mappa (WMS/WMTS/XYZ, vedi MapService._authenticatedTileLoader): impostato via HttpContext
    // sul singolo request invece che via match sull'url, perché l'url del proxy è configurabile da DB
    // (map.collection/layer config) e non garantito uguale in ogni ambiente/cliente. Passavano per
    // XMLHttpRequest grezzo (fuori dalla pipeline HttpClient/interceptor) prima del fix CAST che le ha
    // spostate su HttpClient — effetto collaterale non voluto: spinner che lampeggia ad ogni tile
    const isSkippedBySender = req.context.get(SKIP_LOADING_SPINNER);
    // le richieste escluse/skippate non entrano proprio in "requests": altrimenti removeRequest le conta
    // comunque (this.requests.length > 0) e riaccende lo spinner quando finiscono, anche se nessuna di
    // loro lo aveva mai acceso — bastano altre tile ancora in coda a tenere l'array non vuoto
    const shouldTrack = !isUrlExcludedFromLoadingSpinner && !isSkippedBySender;
    if (shouldTrack) {
      this.requests.push(req);
      this.loaderService.isLoading.next(true);
    }
    // --------------------------------------------------------------------------------------------------------------------
    return new Observable((observer) => {
      const subscription = next.handle(req).subscribe({
        next: (event) => {
          if (event instanceof HttpResponse) {
            observer.next(event);
          }
        },
        error: (err: HttpErrorResponse) => {
          const isLoginRequest = url.includes('auth/login');
          const isAnteaRequest = url.includes('/controlli/antea/');
          if (err.status == 401 && !isLoginRequest && !isAnteaRequest) {
            this.loginService.clearToken();
            this.navigation.clearHistorySearch();
            this.router.navigate(['/login']);
            setTimeout(() => {
              this.ebwModalService.closeAllDialogs();
              this.ebwModalService.openDialog(
                {
                  type: EBWModalType.Warning,
                  title: this.i18nService.getInstantValue('common.expired_session'),
                  content: this.i18nService.getInstantValue('common.relog')
                },
                {
                  autoFocus: false,
                  restoreFocus: false,
                  disableClose: true
                }
              );
            }, 0);
          }
          observer.error(err);
        },
        complete: () => {
          observer.complete();
        }
      });

      return () => {
        // simmetrico al push in ingresso: una richiesta mai tracciata non va mai rimossa da "requests"
        if (shouldTrack) this.removeRequest(req);
        subscription.unsubscribe();
      };
    });
  }
}
